
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component({
	name: 'CustomIonInfiniteScroll',
})
export default class CustomIonInfiniteScroll extends Vue {
	private thrPx = 0;
	private thrPc = 0;
	private scrollEl?: HTMLElement;
	private isLoading = false;

	/**
	 * The threshold distance from the bottom
	 * of the content to call the `infinite` output event when scrolled.
	 * The threshold value can be either a percent, or
	 * in pixels. For example, use the value of `10%` for the `infinite`
	 * output event to get called when the user has scrolled 10%
	 * from the bottom of the page. Use the value `100px` when the
	 * scroll is within 100 pixels from the bottom of the page.
	 */
	@Prop({ default: '15%' }) threshold: string;

	@Watch('threshold')
	protected thresholdChanged() {
		const val = this.threshold;
		if (val.lastIndexOf('%') > -1) {
			this.thrPx = 0;
			this.thrPc = parseFloat(val) / 100;
		} else {
			this.thrPx = parseFloat(val);
			this.thrPc = 0;
		}
	}

	/**
	 * If `true`, the infinite scroll will be hidden and scroll event listeners
	 * will be removed.
	 *
	 * Set this to true to disable the infinite scroll from actively
	 * trying to receive new data while scrolling. This is useful
	 * when it is known that there is no more data that can be added, and
	 * the infinite scroll is no longer needed.
	 */
	@Prop({ default: false }) disabled: boolean;

	@Prop({ default: false }) spinner: boolean;

	@Prop({ default: null }) scrollElement: HTMLElement;

	@Watch('disabled')
	protected disabledChanged() {
		const disabled = this.disabled;
		if (disabled) {
			this.reset();
		} else {
			// We make sure that we're not missing content.
			this.refresh();
		}
		this.enableScrollEvents(!disabled);
	}

	async mounted() {
		const contentEl = this.$el.closest('ion-content') || this.$el.closest('.content');
		if (!contentEl) {
			console.error('<CustomIonInfiniteScroll> must be used inside a ion-content or .content');
			return;
		}

		this.scrollEl = contentEl as any;
		this.thresholdChanged();
		this.enableScrollEvents(!this.disabled);
	}

	beforeDestroy() {
		this.enableScrollEvents(false);
		this.scrollEl = undefined;
	}

	private scrollingRetries = 0;
	private currentListLen = 0;
	private lastListLen = 0;
	private isNotGrowing = false;

	reset() {
		// We don't reset isLoading because it would open the door to race conditions.
		// this.isLoading = false;
		this.isNotGrowing = false;
		this.currentListLen = 0;
		this.lastListLen = 0;
		this.scrollingRetries = 0;
	}

	refresh() {
		const scrollEl = this.scrollEl;
		if (!scrollEl || !this.canStart()) {
			return 1;
		}

		let distanceFromTrigger;
		const height = this.scrollElement ? this.scrollElement.offsetHeight : window.innerHeight;
		const threshold = this.thrPc !== 0 ? height * this.thrPc : this.thrPx;

		const scrollPosition = this.scrollElement ? this.scrollElement.scrollTop : window.scrollY;

		distanceFromTrigger = (this.$el as HTMLElement).offsetTop - threshold - scrollPosition - height;

		if (distanceFromTrigger < 0) {
			if (this.isLoading) {
				return 4;
			}
			this.isLoading = true;

			// We check that the new pages are making the scrollable content grow.
			// We retry (2 times for now, needed for the watchlist view) before blocking completely the infinite scroll.
			if (this.scrollingRetries < 2) {
				if (this.currentListLen === this.lastListLen) {
					this.scrollingRetries++;
				} else {
					this.scrollingRetries = 0;
				}
			} else {
				this.isNotGrowing = true;
				this.isLoading = false;
				return 2;
			}

			this.$emit('ionInfinite', {
				target: this,
			});
			return 3;
		}

		return 4;
	}

	/**
	 * Call `complete()` within the `ionInfinite` output event handler when
	 * your async operation has completed. For example, the `loading`
	 * state is while the app is performing an asynchronous operation,
	 * such as receiving more data from an AJAX request to add more items
	 * to a data list. Once the data has been received and UI updated, you
	 * then call this method to signify that the loading has completed.
	 * This method will change the infinite scroll's state from `loading`
	 * to `enabled`.
	 */
	async complete(listLen: number) {
		const scrollEl = this.scrollEl;
		if (!this.isLoading || !scrollEl) {
			return;
		}
		this.isLoading = false;

		if (listLen) {
			this.lastListLen = this.currentListLen;
			this.currentListLen = listLen;
		}

		// We make sure that after the data fetching is complete, we're still not missing content.
		this.refresh();
	}

	private canStart(): boolean {
		// We need the component.
		if (!this.scrollEl) {
			return false;
		}
		// We need the component to be displayed.
		if (this.scrollEl.offsetHeight === 0 && this.scrollEl.offsetWidth === 0) {
			return false;
		}
		return !this.disabled && !this.isNotGrowing && !this.isLoading;
	}

	private enableScrollEvents(shouldListen: boolean) {
		const scrollEl = this.scrollElement ?? window;
		if (scrollEl) {
			if (shouldListen) {
				scrollEl.addEventListener('scroll', this.refresh);
			} else {
				scrollEl.removeEventListener('scroll', this.refresh);
			}
		}
	}
}
